import Block from '@components/Layout/Block';
import { color, font, query, space } from '@design/templateFns';
import { ChevronLeft, ChevronRight } from '@icon/Chevrons';
import { FC, useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import styled from 'styled-components';
const Noop = () => null;

//import {ResolvedImageData} from '@data/types';
import EVImg from '@components/EVImg';
import AspectBox from '@components/Layout/AspectBox';

const Wrapper = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: column-reverse;
  margin-left: ${space('marginMobile')};
  margin-right: ${space('marginMobile')};
  @media screen and (${query.atLeast('tablet')}) {
    gap: 157px;
    flex-direction: row;
    justify-content: center;
    margin: 0 auto;
  }

  @media (max-width: 1200px) {
    gap: 40px;
  }
`;

const ImageWrapper = styled.div`
  width: 100%;
  @media screen and (${query.atLeast('tablet')}) {
    width: 516px;
    height: 387px;
  }
`;

const CarouselWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;

  @media screen and (${query.atLeast('tablet')}) {
    justify-content: center;
    max-width: 400px;
    a {
      width: 120px;
    }
  }
`;

const Headline = styled.h2`
  ${font('headline-xs')};
  color: ${color('fg')};
  margin-bottom: 40px;
  padding: 0;
  text-align: center;
  @media screen and (${query.atLeast('tablet')}) {
    ${font('headline-md')};
    margin-bottom: 84px;
  }
`;

const Eyebrow = styled.h3`
  ${font('eyebrow-md')};
  font-size: 14px;
  text-transform: uppercase;
  text-align: center;
  color: ${color('cta')};
`;

const SlideTitle = styled.h3`
  ${font('headline-xxs')};
  color: ${color('fg')};
  padding: 0;
  margin-bottom: 32px;
  @media screen and (${query.atLeast('tablet')}) {
    ${font('headline-sm')};
  }
`;

const SlideText = styled.p`
  ${font('body-md-light')};
  margin-bottom: 25px;
`;

// For use in Story
export const StoryWrapper = styled.div`
  display: flex;
`;

//Carousel

const CarouselContainer = styled.div`
  width: 100%;
`;

const CarouselNav = styled.div`
  display: flex;
  align-items: left;
  @media screen and (${query.below('tablet')}) {
    align-items: center;
    justify-content: center;
  }
`;

const CarouselArrow = styled.div<{ $right?: boolean }>`
  position: relative;
  margin-right: ${({ $right }) => ($right ? '5px' : '0')};
  margin-left: ${({ $right }) => ($right ? '0' : '5px')};
  height: 36px;
  width: 36px;
  border-radius: 50%;
  cursor: pointer;
  border: 1px solid #e5e5e5;

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-right: -50%;
    transform: translate(-50%, -50%);
  }
`;

export const ListContainer = styled.div`
  position: relative;
  width: 100%;
  overflow-x: hidden;
  padding-bottom: 0;
  /* mobile single slide carousel */
  padding-right: 0;
  /* larger sizes */
  .slick-list {
    overflow: hidden;
  }
  .slick-track {
    display: flex;
    flex-wrap: nowrap;
    z-index: 4;
  }
  .slick-slide {
    // position: relative !important;
    display: flex;
    margin-left: 12px;
    margin-right: 12px;
  }
  .slick-slide > div {
    display: flex;
    flex: 0 0 100%;
  }
  .slick-dots {
    display: none;
  }
  .slick-dots li button {
    display: none;
  }
`;

export const CarouselSlot = styled.span`
  position: relative !important;
`;

const SlideBlock = styled.div`
  width: 400px;
`;

export interface ValuePropCarouselProps {
  eyebrow: string;
  header: string;
  backgroundColor: boolean;
  valuePropCardContent: {
    cardHeadline: string;
    cardDescription: string;
    cardImage: string;
  }[];
}

export const ValuePropCarousel: FC<ValuePropCarouselProps> = (props) => {
  const { eyebrow, header, backgroundColor, valuePropCardContent } = props;

  const [index, setIndex] = useState(0);
  const [isPrevDisabled, setIsPrevDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(false);

  const ref = useRef({
    slickNext: () => {},
    slickPrev: () => {},
    innerSlider: {
      state: {
        currentSlide: 1,
      },
    },
  });

  const onPrev = (e) => {
    e.preventDefault();
    ref.current.slickPrev();

    const imageIndex = index === 0 ? index : index - 1;
    const disabled = ref.current?.innerSlider?.state?.currentSlide <= 1 ? true : false;
    setIndex(imageIndex);
    setIsPrevDisabled(disabled);
    setIsNextDisabled(false);
  };

  const onNext = (e) => {
    e.preventDefault();
    ref.current.slickNext();

    const imageIndex = index === valuePropCardContent.length - 1 ? index : index + 1;
    const disabled =
      ref.current?.innerSlider?.state?.currentSlide >= valuePropCardContent.length - 2
        ? true
        : false;
    setIndex(imageIndex);
    setIsNextDisabled(disabled);
    setIsPrevDisabled(false);
  };

  useEffect(() => {
    if (valuePropCardContent && valuePropCardContent.length == 1) {
      setIsNextDisabled(true);
      setIsPrevDisabled(true);
    }
  }, []);

  //there are always three client testimonials

  const SliderConfig = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    prevArrow: <Noop />,
    nextArrow: <Noop />,
    easing: 'ease-in-out',
    slidesToScroll: 1,
    variableWidth: true,
    mobileFirst: true,
  };
  return (
    <Block fullOnMobile background={backgroundColor ? 'secondaryBg' : 'bg'}>
      <Eyebrow>{eyebrow}</Eyebrow>
      <Headline>{header}</Headline>
      <Wrapper>
        <CarouselWrapper>
          <CarouselContainer>
            <ListContainer ref={ref} as={Slider} {...SliderConfig}>
              {valuePropCardContent &&
                valuePropCardContent.map(function (slide, index) {
                  return (
                    <CarouselSlot key={index}>
                      <SlideBlock>
                        <SlideTitle>{slide.cardHeadline}</SlideTitle>
                        <SlideText>{slide.cardDescription}</SlideText>
                      </SlideBlock>
                    </CarouselSlot>
                  );
                })}
            </ListContainer>
            <CarouselNav>
              <CarouselArrow onClick={(e) => onPrev(e)} $right>
                <ChevronLeft color={isPrevDisabled ? '#ddd' : '#383737'} />
              </CarouselArrow>
              <CarouselArrow onClick={(e) => onNext(e)}>
                <ChevronRight color={isNextDisabled ? '#ddd' : '#383737'} />
              </CarouselArrow>
            </CarouselNav>
          </CarouselContainer>
        </CarouselWrapper>
        <ImageWrapper>
          <AspectBox $ratio={1.3333}>
            <EVImg
              src={valuePropCardContent[index]?.cardImage}
              sizes={['516px', '516px', '516px', '516px']}
              unoptimized={true}
              objectFit="contain"
            />
          </AspectBox>
        </ImageWrapper>
      </Wrapper>
    </Block>
  );
};

export default ValuePropCarousel;
